import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Download } from 'lucide-react';
import ReportService from '../services/ReportService';
import APIRequest from '../api/APIRequest';
import { useSelector } from 'react-redux';
import Alert from '../components/ui/Alert';
import Loader from '../components/ui/Loader';
import AttendanceCalendar from '../components/attendance/AttendanceCalendar';
import AttendanceSummary from '../components/attendance/AttendanceSummary';
import MonthPicker from '../components/attendance/MonthPicker';
import CalendarManagement from '../components/attendance/CalendarManagement';
import { useToast } from '../contexts/ToastContext';

const TeachersAttendance = () => {
  const { user } = useSelector((state) => state.auth);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const { success, error } = useToast();


  const fetchTeachers = useCallback(async () => {
    setApiLoading(true);
    try {
      const apiRequest = new APIRequest();
      const [teachersResponse, classesResponse] = await Promise.all([
        apiRequest.request("/admin/getAllTeachers", "GET", null, user?.token),
        apiRequest.request("/admin/getAllClasses", "GET", null, user?.token)
      ]);
      
      if (teachersResponse.success) {
        setTeachers(teachersResponse.teachers || []);
      }
      
      if (classesResponse.success) {
        setClasses(classesResponse.classes || []);
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setAlert({ type: 'error', message: 'Failed to fetch teachers' });
    } finally {
      setApiLoading(false);
    }
  }, [user?.token]);

  const fetchMonthlyAttendance = useCallback(async (month) => {
    if (!month) return;
    
    setApiLoading(true);
    try {
      const apiRequest = new APIRequest();
      const payload = {
        month: month // Send the month directly (YYYY-MM format)
      };
      const response = await apiRequest.request(`/admin/getTeachersMonthlyAttendance`, 'POST', payload, user?.token);
      
      if (response.success) {
        // Transform the data to match our expected format
        const attendanceRecord = response.data;
        if (attendanceRecord && attendanceRecord.teachers) {
          // Calculate actual calendar days in the selected month
          const daysInMonth = getDaysInMonth(selectedMonth);
          
          const teacherSummary = attendanceRecord.teachers.map(teacher => ({
            teacherId: teacher.id,
            totalDays: daysInMonth, // Use actual calendar days instead of attendance records
            presentDays: teacher.attendance.filter(a => a.action === 'present').length,
            absentDays: teacher.attendance.filter(a => a.action === 'absent').length,
            halfDays: teacher.attendance.filter(a => a.action === 'half').length,
            attendance: teacher.attendance
          }));
          
          setAttendanceData({
            studentSummary: teacherSummary, // Use studentSummary for compatibility
            teacherSummary: teacherSummary,
            attendanceByDate: {},
            totalTeachers: teacherSummary.length
          });
        } else {
          // No attendance data, but we still want to show the summary with correct total days
          const daysInMonth = getDaysInMonth(selectedMonth);
          const teacherSummary = teachers.map(teacher => ({
            teacherId: teacher._id,
            totalDays: daysInMonth,
            presentDays: 0,
            absentDays: 0,
            halfDays: 0,
            attendance: []
          }));
          
          setAttendanceData({
            studentSummary: teacherSummary, // Use studentSummary for compatibility
            teacherSummary: teacherSummary,
            attendanceByDate: {},
            totalTeachers: teacherSummary.length
          });
        }
      } else {
        setAttendanceData(null);
      }
    } catch (error) {
      console.error('Error fetching monthly attendance:', error);
      setAlert({ type: 'error', message: 'Failed to fetch attendance data' });
      setAttendanceData(null);
    } finally {
      setApiLoading(false);
    }
  }, [user?.token, selectedMonth, teachers]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);


  // Helper function to get month start and end dates
  const getMonthStartEnd = useCallback((month) => {
    const [year, monthNum] = month.split('-').map(Number);
    const startDate = new Date(year, monthNum - 1, 1);
    const endDate = new Date(year, monthNum, 0);
    endDate.setHours(23, 59, 59, 999);
    return { startDate, endDate };
  }, []);

  const fetchCalendarEvents = useCallback(async (month) => {
    if (!month) return;
    try {
      const { startDate, endDate } = getMonthStartEnd(month);
      const apiRequest = new APIRequest();
      // Pass empty classId for global events/holidays
      const response = await apiRequest.request(
        `/admin/calendar/get-events?startDate=${startDate.getTime()}&endDate=${endDate.getTime()}&classId=`,
        'GET',
        null,
        user?.token
      );
      if (response.success) {
        setCalendarEvents(response.data);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
    }
  }, [user?.token, getMonthStartEnd]);

  useEffect(() => {
    if (selectedMonth) {
      fetchMonthlyAttendance(selectedMonth);
      fetchCalendarEvents(selectedMonth);
    }
  }, [selectedMonth, fetchMonthlyAttendance, fetchCalendarEvents]);

  // Helper function to get days in month
  const getDaysInMonth = (month) => {
    const [year, monthNum] = month.split('-').map(Number);
    return new Date(year, monthNum, 0).getDate();
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
  };

  // Get attendance status for a specific teacher and date
  const getTeacherAttendanceForDate = (teacherId, date) => {
    // Check both teacherSummary and studentSummary for compatibility
    const teacherSummary = attendanceData?.teacherSummary?.find(t => t.teacherId === teacherId) ||
                          attendanceData?.studentSummary?.find(t => t.teacherId === teacherId);
    
    if (!teacherSummary) return null;

    // Convert input date to YYYY-MM-DD format
    const targetObj = new Date(date);
    const targetKey = `${targetObj.getFullYear()}-${targetObj.getMonth()}-${targetObj.getDate()}`;
    
    const attendanceEntry = teacherSummary?.attendance?.find(a => {
      let entryDate;
      if (typeof a.date === 'string' && !isNaN(a.date) && a.date.trim() !== '') {
          entryDate = new Date(parseInt(a.date));
      } else if (typeof a.date === 'number') {
          entryDate = new Date(a.date);
      } else {
          entryDate = new Date(a.date);
      }
      const entryKey = `${entryDate.getFullYear()}-${entryDate.getMonth()}-${entryDate.getDate()}`;
      return entryKey === targetKey;
    });
   
    return attendanceEntry ? attendanceEntry?.action : null;
  };

  // Update teacher attendance for multiple dates
  const updateTeacherAttendance = async (updates) => {
    if (!updates || updates.length === 0) return;
    
    setApiLoading(true);
    try {
      const apiRequest = new APIRequest();
      const { startDate, endDate } = getMonthStartEnd(selectedMonth);
      
      // Debug: Log the actual updates structure
      console.log('Received updates:', updates);
      console.log('First update structure:', updates[0]);
      
      // Validate updates before processing
      const validUpdates = updates.filter(update => {
        console.log('Checking update:', update);
        console.log('update.studentId:', update.studentId);
        console.log('update.date:', update.date);
        console.log('update.status:', update.status);
        
        // The AttendanceCalendar sends { studentId, date, status }
        if (!update.studentId || !update.date || !update.status) {
          console.warn('Invalid update entry:', update);
          return false;
        }
        if (!['present', 'absent', 'half'].includes(update.status)) {
          console.warn('Invalid status:', update.status);
          return false;
        }
        return true;
      });

      if (validUpdates.length === 0) {
        error('No valid attendance updates to process');
        setApiLoading(false);
        return;
      }
      
      // Group updates by teacher
      const teacherUpdates = {};
      validUpdates.forEach(update => {
        // Use studentId as teacherId (since AttendanceCalendar uses studentId for both)
        const teacherId = update.studentId;
        if (!teacherUpdates[teacherId]) {
          teacherUpdates[teacherId] = [];
        }
        teacherUpdates[teacherId].push({
          date: new Date(update.date).getTime().toString(), // Send as milliseconds
          action: update.status // Use status as action
        });
      });

      // Convert to API format
      const teachers = Object.entries(teacherUpdates).map(([teacherId, attendance]) => ({
        id: teacherId,
        attendance: attendance
      }));

      const attendanceData = {
        startDate: startDate.getTime().toString(), // Send as milliseconds
        endDate: endDate.getTime().toString(), // Send as milliseconds
        teachers: teachers
      };

      const response = await apiRequest.request("/admin/bulkUpdateTeacherAttendance", "POST", attendanceData, user?.token);
            
      if (response.success) {
        const actionCounts = validUpdates.reduce((acc, update) => {
          acc[update.status] = (acc[update.status] || 0) + 1;
          return acc;
        }, {});
        
        const actionText = Object.entries(actionCounts)
          .map(([action, count]) => `${count} ${action}`)
          .join(', ');
        
        success(`Successfully updated ${validUpdates.length} attendance record(s): ${actionText}`);
        // Refresh attendance data
        fetchMonthlyAttendance(selectedMonth);
      } else {
        console.error('API Error Response:', response);
        error(response.message || 'Failed to update attendance');
      }
    } catch (err) {
      console.error('Error updating teacher attendance:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response?.data
      });
      error('Failed to update teacher attendance: ' + (err.message || 'Unknown error'));
    } finally {
      setApiLoading(false);
    }
  };

  // Get attendance color for calendar cell
  const getAttendanceColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-500';
      case 'absent':
        return 'bg-red-500';
      case 'half':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-300';
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(selectedMonth);
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };


  const handleRefresh = useCallback(() => {
    if (selectedMonth) {
      fetchMonthlyAttendance(selectedMonth);
    }
  }, [selectedMonth, fetchMonthlyAttendance]);

  if (apiLoading && !attendanceData) {
    return <Loader />;
  }

  return (
    <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto">
      {/* Modern Header Section */}
      <div className="glass-card rounded-[2.5rem] p-8 px-10 flex flex-col md:flex-row justify-between items-center gap-6 premium-shadow">
        <div className="text-center md:text-left">
          <h1 className="text-4xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
              Teachers Attendance
            </span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium flex items-center justify-center md:justify-start gap-2">
            <Calendar size={18} className="text-[#1B6CA8]" />
            Track and manage teacher attendance
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <div className="flex bg-slate-100 rounded-2xl p-1 shadow-sm">
            <button
              onClick={() => {
                if (!attendanceData?.teacherSummary) {
                  return error('No attendance data available for the selected month');
                }
                const days = generateCalendarDays();
                const columns = [
                  { header: 'Teacher Name', dataKey: 'name', width: 45 },
                  ...days.map(d => ({ header: d.toString(), dataKey: `day_${d}`, width: 7 }))
                ];
                const data = attendanceData.teacherSummary.map(t => {
                  const teacher = teachers.find(teach => (teach._id === t.teacherId || teach.id === t.teacherId));
                  const row = { name: teacher?.name || 'N/A' };
                  days.forEach(d => {
                    const dayAttendance = t.attendance?.find(a => new Date(a.date).getDate() === d);
                    const status = dayAttendance?.action;
                    row[`day_${d}`] = status === 'present' ? 'P' : status === 'absent' ? 'A' : status === 'half' ? 'H' : '-';
                  });
                  return row;
                });
                const monthName = new Date(selectedMonth).toLocaleString('default', { month: 'long', year: 'numeric' });
                ReportService.generatePDF(
                  `Teacher Attendance - (${monthName})`,
                  columns,
                  data,
                  `teacher_attendance_${monthName}.pdf`,
                  'landscape'
                );
              }}
              disabled={!attendanceData?.teacherSummary}
              className={`px-4 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm ${
                !attendanceData?.teacherSummary 
                  ? 'opacity-50 cursor-not-allowed text-slate-400' 
                  : 'text-[#1e293b] hover:bg-white text-blue-600'
              }`}
            >
              <Download size={16} className={!attendanceData?.teacherSummary ? 'text-slate-400' : 'text-blue-600'} />
              <span>PDF</span>
            </button>
            <button
              onClick={() => {
                if (!attendanceData?.teacherSummary) {
                  return error('No attendance data available for the selected month');
                }
                const days = generateCalendarDays();
                const data = attendanceData.teacherSummary.map(t => {
                  const teacher = teachers.find(teach => (teach._id === t.teacherId || teach.id === t.teacherId));
                  const row = { 'Teacher Name': teacher?.name || 'N/A' };
                  days.forEach(d => {
                    const dayAttendance = t.attendance?.find(a => new Date(a.date).getDate() === d);
                    const status = dayAttendance?.action;
                    row[d] = status === 'present' ? 'P' : status === 'absent' ? 'A' : status === 'half' ? 'H' : '-';
                  });
                  return row;
                });
                const monthName = new Date(selectedMonth).toLocaleString('default', { month: 'long', year: 'numeric' });
                ReportService.generateExcel(data, `teacher_attendance_${monthName}.xlsx`, ['Teacher Name', ...days]);
              }}
              disabled={!attendanceData?.teacherSummary}
              className={`px-4 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm ${
                !attendanceData?.teacherSummary 
                  ? 'opacity-50 cursor-not-allowed text-slate-400' 
                  : 'text-[#1e293b] hover:bg-white text-emerald-600'
              }`}
            >
              <Download size={16} className={!attendanceData?.teacherSummary ? 'text-slate-400' : 'text-emerald-600'} />
              <span>Excel</span>
            </button>
          </div>
        </div>
      </div>

      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}


      <div className="glass-card rounded-[2rem] p-8 premium-shadow border-none animate-slideInUp">
        <div className="flex flex-col space-y-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h3 className="text-xl font-black text-slate-800 tracking-tight">Filters</h3>
              <p className="text-slate-400 text-sm font-medium">Select month to view attendance</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Month Selection</label>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <MonthPicker
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    disabled={apiLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* Event Management - Visible when month is selected */}
        {selectedMonth && (
          <div className="relative z-10 animate-fadeIn mb-8">
            <CalendarManagement 
              selectedMonth={selectedMonth}
              classId="" // Global events for teachers
              onEventChange={() => {
                fetchCalendarEvents(selectedMonth);
                fetchMonthlyAttendance(selectedMonth);
              }}
            />
          </div>
        )}

        {/* Teachers Summary */}
        {/* <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Teachers Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{teachers.length}</div>
              <div className="text-sm text-gray-600">Total Teachers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {teachers.filter(teacher => classes.some(cls => cls.classIncharge === teacher._id)).length}
              </div>
              <div className="text-sm text-gray-600">Class Incharges</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {teachers.filter(teacher => teacher.gender === 'male').length}
              </div>
              <div className="text-sm text-gray-600">Male Teachers</div>
            </div>
          </div>
        </div> */}

        {attendanceData && (
          <div className="animate-fadeIn">
            <AttendanceSummary
              attendanceData={attendanceData}
              students={teachers}
              type="teachers"
            />
          </div>
        )}

        {attendanceData && (
          <div className="mt-8 animate-slideInUp">
            <AttendanceCalendar
              students={teachers}
              selectedMonth={selectedMonth}
              attendanceData={attendanceData}
              calendarEvents={calendarEvents}
              getStudentAttendanceForDate={getTeacherAttendanceForDate}
              getAttendanceColor={getAttendanceColor}
              generateCalendarDays={generateCalendarDays}
              updateStudentAttendance={updateTeacherAttendance}
              apiLoading={apiLoading}
              onRefresh={handleRefresh}
              searchPlaceholder="Search staff..."
            />
          </div>
        )}
    </div>
  );
};

export default TeachersAttendance;
